import { Search, Copy, Download, AlignJustify, Clock, ArrowDown } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface LogViewerControlsProps {
  /** Current filter value */
  filter: string
  /** Filter change handler */
  onFilterChange: (value: string) => void
  /** Whether timestamps are shown */
  showTimestamps: boolean
  /** Timestamps toggle handler */
  onShowTimestampsChange: (value: boolean) => void
  /** Whether auto-scroll is enabled */
  autoScroll: boolean
  /** Auto-scroll toggle handler */
  onAutoScrollChange: (value: boolean) => void
  /** Whether lines wrap */
  wrapLines: boolean
  /** Wrap lines toggle handler */
  onWrapLinesChange: (value: boolean) => void
  /** Copy handler */
  onCopy?: () => void
  /** Download handler */
  onDownload?: () => void
  /** Total number of log lines */
  totalLines: number
  /** Filtered number of log lines */
  filteredLines: number
  /** Additional CSS classes */
  className?: string
}

/**
 * Control bar for log viewer with filter, toggles, and actions.
 * 
 * @example
 * ```tsx
 * <LogViewerControls
 *   filter={filter}
 *   onFilterChange={setFilter}
 *   showTimestamps={showTimestamps}
 *   onShowTimestampsChange={setShowTimestamps}
 *   autoScroll={autoScroll}
 *   onAutoScrollChange={setAutoScroll}
 *   wrapLines={wrapLines}
 *   onWrapLinesChange={setWrapLines}
 *   onCopy={handleCopy}
 *   onDownload={handleDownload}
 *   totalLines={1234}
 *   filteredLines={567}
 * />
 * ```
 */
export function LogViewerControls({
  filter,
  onFilterChange,
  showTimestamps,
  onShowTimestampsChange,
  autoScroll,
  onAutoScrollChange,
  wrapLines,
  onWrapLinesChange,
  onCopy,
  onDownload,
  totalLines,
  filteredLines,
  className,
}: LogViewerControlsProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 p-2 border-b bg-muted/30',
        className
      )}
    >
      {/* Search/Filter */}
      <div className="relative flex-1 max-w-xs">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Filter logs..."
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          className="pl-8 h-8"
        />
      </div>

      {/* Line count */}
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        {filter
          ? `${filteredLines.toLocaleString()} / ${totalLines.toLocaleString()} lines`
          : `${totalLines.toLocaleString()} lines`}
      </span>

      <div className="flex-1" />

      {/* Toggle buttons */}
      <Button
        variant={showTimestamps ? 'secondary' : 'ghost'}
        size="icon"
        className="h-8 w-8"
        onClick={() => onShowTimestampsChange(!showTimestamps)}
        title="Toggle timestamps"
      >
        <Clock className="h-4 w-4" />
      </Button>

      <Button
        variant={wrapLines ? 'secondary' : 'ghost'}
        size="icon"
        className="h-8 w-8"
        onClick={() => onWrapLinesChange(!wrapLines)}
        title="Toggle line wrap"
      >
        <AlignJustify className="h-4 w-4" />
      </Button>

      <Button
        variant={autoScroll ? 'secondary' : 'ghost'}
        size="icon"
        className="h-8 w-8"
        onClick={() => onAutoScrollChange(!autoScroll)}
        title="Toggle auto-scroll"
      >
        <ArrowDown className="h-4 w-4" />
      </Button>

      <div className="h-4 w-px bg-border" />

      {/* Action buttons */}
      {onCopy && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onCopy}
          title="Copy logs"
        >
          <Copy className="h-4 w-4" />
        </Button>
      )}

      {onDownload && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onDownload}
          title="Download logs"
        >
          <Download className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
